// SPDX-FileCopyrightText: 2022-2026 Lablup Inc. All rights reserved.
// SPDX-License-Identifier: LicenseRef-Proprietary
import _ from "es-toolkit/compat";
import { atom, useAtomValue } from "jotai";
import { atomWithStorage } from "jotai/utils";

import { currentUserAtom } from ".";
import { fetchJson } from "../../infra/fetchJson";
import { minifyGraphQLQuery } from "../minifyGraphQLQuery";

export interface Project {
  id: string;
  name: string;
}

export const projectListAtom = atom((get) => {
  const user = get(currentUserAtom);
  return (user?.groups ?? []) as Project[];
});

export const availableProjectListAtom = atom(async (get) => {
  const user = get(currentUserAtom);
  if (user === null) {
    return [] as Project[];
  }
  const response = await fetchJson<{
    data: {
      user: {
        groups: Project[];
      } | null;
    } | null;
  }>("/func/admin/gql", {
    method: "POST",
    body: JSON.stringify({
      // WARN: the result of this query has only scaling_groups allowed to the current project
      query: minifyGraphQLQuery(`
        query {
          user {
            username
            groups {
              id
              name
            }
          }
        }
      `),
      variables: {},
    }),
  });
  return (
    response.data?.user?.groups?.filter(
      // NOTE: Manager does not support `user_nodes { group_nodes { name } }`.
      (group) => group.name !== "model-store"
    ) ?? []
  );
});

export const projectIdAtom = atomWithStorage<string>(
  "ai.backend.fasttrack.currentProjectId",
  "",
  undefined,
  { getOnInit: true }
);

export const projectAtom = atom((get) => {
  const projectList = get(projectListAtom);
  const projectId = get(projectIdAtom);
  return _.find(projectList, (project) => project.id === projectId);
});

export const useProjectId = (
  projectName?: string | null
): string | undefined => {
  const projectList = useAtomValue(projectListAtom);
  const project = _.find(
    projectList,
    (project) => project.name === projectName
  ) as Project | undefined;
  return project?.id;
};
